import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import type { AdminTheme } from '@/context/AdminThemeContext';
import type { TransactionItem } from '@/types/transactions';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import {
  formatTransactionAmount,
  formatTransactionNaira,
  getCategoryTone,
  getTransactionStatusTone,
} from '@/utils/transactionFormatters';

interface AdminTransactionTableProps {
  transactions: TransactionItem[];
  theme: AdminTheme;
  detailPath: (id: number) => string;
}

export function AdminTransactionTable({ transactions, theme, detailPath }: AdminTransactionTableProps) {
  const shell = adminShell(theme);

  return (
    <div className={cn('overflow-x-auto rounded-xl border', shell.tableWrap, shell.card)}>
      <table className="w-full min-w-[920px] text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', shell.tableHead, shell.tableWrap)}>
            <th className="px-4 py-3 font-semibold">Reference</th>
            <th className="px-4 py-3 font-semibold">User</th>
            <th className="px-4 py-3 font-semibold">Service</th>
            <th className="px-4 py-3 font-semibold">Category</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 text-right font-semibold">Amount</th>
            <th className="px-4 py-3 text-right font-semibold">Profit</th>
            <th className="px-4 py-3 font-semibold">Date</th>
            <th className="px-4 py-3 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr key={tx.id} className={cn('border-b transition-colors', shell.tableRow, shell.tableWrap)}>
              <td className={cn('px-4 py-3 font-mono text-xs', shell.tableCell)}>{tx.reference}</td>
              <td className={cn('px-4 py-3', shell.tableCell)}>
                {tx.user ? (
                  <div>
                    <p className="font-medium">
                      {tx.user.firstName} {tx.user.lastName}
                    </p>
                    <p className={cn('text-xs', shell.cardMuted)}>{tx.user.phone}</p>
                  </div>
                ) : (
                  '—'
                )}
              </td>
              <td className={cn('px-4 py-3', shell.tableCell)}>
                <p className="font-medium">{tx.serviceName}</p>
                <p className={cn('text-xs', shell.cardMuted)}>{tx.kindLabel}</p>
              </td>
              <td className="px-4 py-3">
                <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase', getCategoryTone(tx.category))}>
                  {tx.categoryLabel}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize', getTransactionStatusTone(tx.status))}>
                  {tx.status}
                </span>
              </td>
              <td className={cn('px-4 py-3 text-right font-semibold tabular-nums', shell.tableCell)}>
                {formatTransactionAmount(tx)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-500">
                {tx.profit != null ? formatTransactionNaira(tx.profit) : '—'}
              </td>
              <td className={cn('px-4 py-3 text-xs tabular-nums', shell.cardMuted)}>
                {new Date(tx.createdAt).toLocaleString('en-NG', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={detailPath(tx.id)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors',
                    shell.outlineBtn,
                  )}
                >
                  <Eye className="h-3.5 w-3.5" />
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
